import React, { ComponentProps, useCallback, useEffect, useState } from 'react';
import 'video.js/dist/video-js.min.css'; // keep the CSS here (safe to preload)
import './dash-player.scss';

interface Props extends ComponentProps<'div'> {
    src: string;
    size?: 'fill' | 'fluid' | 'none';
    autoPlay?: boolean;
    muted?: boolean;
    loop?: boolean;
    controls?: boolean;
}

function DashPlayer({
    src,
    size = 'fluid',
    autoPlay = true,
    muted = true,
    loop = false,
    controls = true,
    ...divProps
}: Props) {
    const [player, setPlayer] = useState<any>(null);

    const element = useCallback(
        async (node: HTMLVideoElement | null) => {
            if (node) {
                // � Lazy load video.js and dash plugin
                const videojs = (await import('video.js')).default;

                if (src.endsWith('.mpd')) {
                    await import('videojs-contrib-dash');
                }

                const newPlayer = videojs(node, {
                    controls,
                    autoplay: autoPlay,
                    loop,
                    muted, // autoplay requires muted
                    controlBar: {
                        pictureInPictureToggle: false,
                        remainingTimeDisplay: false,
                    },
                    html5: {
                        dash: {
                            updateSettings: {
                                streaming: {
                                    abr: {
                                        limitBitrateByPortal: true,
                                    },
                                },
                            },
                        },
                    },
                });

                newPlayer.ready(() => setPlayer(newPlayer));
            }
        },
        [autoPlay, loop, muted, controls, src]
    );

    useEffect(() => {
        player?.src({ src });
    }, [player, src]);

    useEffect(() => {
        if (!player) return;
        player.fluid(size === 'fluid');
        player.fill(size === 'fill');
    }, [player, size]);

    return (
        <div {...divProps} className={`video-player ${divProps.className ?? ''}`}>
            <video ref={element} className='video-js' />
        </div>
    );
}

export default DashPlayer;
